import {
  quizWorkspaceHasIntegratorApiDraft,
  quizEditionHasPlayableItems,
  type QuizEditionApprovalStatus,
  type QuizEditionId,
  type QuizEditionPointers,
  type QuizEditionRole,
  type QuizEditionSummary,
  type QuizWorkspaceView,
} from './types';
import { resolveRefreshPolicyState } from './refreshPolicy';

export type QuizEditionQuestionsTopology = 'live_only' | 'upcoming_and_live';
export type QuizEditionLiveEditingMode = 'autosave' | 'explicit_publish';
export type QuizEditionPublicationTiming = 'manual' | 'scheduled';

export type ProductUpcomingEdition =
  | {
      state: 'empty';
      publicationTiming: QuizEditionPublicationTiming;
      edition: null;
    }
  | {
      state: 'preparing' | 'awaiting_approval' | 'approval_not_required' | 'approved';
      publicationTiming: QuizEditionPublicationTiming;
      edition: QuizEditionSummary;
    };

export type ManualQuizEditionPublication =
  { state: 'none'; edition: null } | { state: 'awaiting_publication'; edition: QuizEditionSummary };

export type ResolvedQuizEditionWorkflow =
  | {
      kind: 'one_off';
      questionsTopology: 'live_only';
      liveEditingMode: 'autosave';
      productUpcoming: null;
      manualPublication: ManualQuizEditionPublication;
    }
  | {
      kind: 'recurring';
      questionsTopology: 'upcoming_and_live';
      liveEditingMode: 'explicit_publish';
      productUpcoming: ProductUpcomingEdition;
      manualPublication: null;
    };

export type QuizEditionWorkflowWorkspace = Pick<
  QuizWorkspaceView,
  'refreshPolicy' | 'sourceConfig' | 'draftEdition' | 'liveEdition'
>;

export interface ResolveQuizEditionWorkflowOptions {
  /** Whether the Upcoming edition has finished preparing enough content for approval. */
  approvalReady?: boolean;
}

export function deriveQuizEditionRole(
  editionId: QuizEditionId,
  pointers: QuizEditionPointers
): QuizEditionRole {
  if (pointers.liveEditionId === editionId) {
    return 'live';
  }
  if (pointers.draftEditionId === editionId) {
    return 'draft';
  }
  return 'past';
}

export function isEditionApproved(status: QuizEditionApprovalStatus): boolean {
  return status === 'approved';
}

/**
 * Resolves the product-facing Quiz Edition workflow without exposing storage pointers as
 * lifecycle. Recurrence intent owns Upcoming/Live topology; configured Schedule owns whether
 * an unapproved Upcoming edition needs a human approval step.
 */
export function resolveQuizEditionWorkflow(
  workspace: QuizEditionWorkflowWorkspace,
  options: ResolveQuizEditionWorkflowOptions = {}
): ResolvedQuizEditionWorkflow {
  const refreshState = resolveRefreshPolicyState(workspace, options);

  if (!refreshState.hasRecurringIntent) {
    // Deleting every Live item keeps its edition pointer. Replacement questions still need
    // a visible draft and an explicit publication action.
    const hasEmptyLiveEdition =
      Boolean(workspace.liveEdition) && !quizEditionHasPlayableItems(workspace.liveEdition);
    const manualPublication =
      workspace.draftEdition &&
      (quizWorkspaceHasIntegratorApiDraft(workspace) || hasEmptyLiveEdition)
        ? {
            state: 'awaiting_publication' as const,
            edition: workspace.draftEdition,
          }
        : { state: 'none' as const, edition: null };

    return {
      kind: 'one_off',
      questionsTopology: 'live_only',
      liveEditingMode: 'autosave',
      productUpcoming: null,
      manualPublication,
    };
  }

  const publicationTiming: QuizEditionPublicationTiming = refreshState.hasRecurringSchedule
    ? 'scheduled'
    : 'manual';
  const draftEdition = workspace.draftEdition;
  let productUpcoming: ProductUpcomingEdition;

  if (!draftEdition) {
    productUpcoming = { state: 'empty', publicationTiming, edition: null };
  } else if (isEditionApproved(draftEdition.approvalStatus)) {
    productUpcoming = { state: 'approved', publicationTiming, edition: draftEdition };
  } else if (refreshState.approvalState === 'preparing_draft') {
    productUpcoming = { state: 'preparing', publicationTiming, edition: draftEdition };
  } else if (refreshState.approvalState === 'awaiting_approval') {
    productUpcoming = { state: 'awaiting_approval', publicationTiming, edition: draftEdition };
  } else {
    productUpcoming = {
      state: 'approval_not_required',
      publicationTiming,
      edition: draftEdition,
    };
  }

  return {
    kind: 'recurring',
    questionsTopology: 'upcoming_and_live',
    liveEditingMode: 'explicit_publish',
    productUpcoming,
    manualPublication: null,
  };
}
